import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { submitSupplierPurchaseOrderRating } from "../../services/supplierRating.service";
import "../../assets/styles/infrastructure_leasing_modal.scss";

function StarRow({ label, value, onChange, required = false }) {
  return (
    <div className="mb-3">
      <div className="ile-form-label">
        <span>
          {label}
          {required ? <span className="text-danger"> *</span> : null}
        </span>
      </div>
      <div className="d-flex flex-wrap gap-1" role="group" aria-label={label}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className={`btn btn-sm ${value === n ? "btn-warning text-dark" : "btn-outline-secondary"}`}
            onClick={() => onChange(n)}
            aria-pressed={value === n}
            aria-label={`${n} stars`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SupplierRatingModal({
  open,
  onClose,
  purchaseOrderId,
  grnId,
  supplierName,
  poNumber,
  onSuccess,
}) {
  const [title, setTitle] = useState("");
  const [rating, setRating] = useState(5);
  const [ratingDelivery, setRatingDelivery] = useState(0);
  const [ratingQuality, setRatingQuality] = useState(0);
  const [ratingCommunication, setRatingCommunication] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const suffix = `${purchaseOrderId || "x"}-${grnId || "y"}`;

  useEffect(() => {
    if (open) {
      setTitle("");
      setRating(5);
      setRatingDelivery(0);
      setRatingQuality(0);
      setRatingCommunication(0);
      setComment("");
      setSubmitting(false);
    }
  }, [open, purchaseOrderId, grnId]);

  if (!open) return null;

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const poId = Number(purchaseOrderId);
    if (!Number.isFinite(poId) || poId < 1) {
      toast.error("Missing purchase order.");
      return;
    }
    if (!rating || rating < 1 || rating > 5) {
      toast.warning("Choose an overall rating from 1 to 5.");
      return;
    }

    setSubmitting(true);
    try {
      const saved = await submitSupplierPurchaseOrderRating(poId, {
        grnId: grnId ? Number(grnId) : null,
        title: title.trim() || undefined,
        rating,
        comment: comment.trim() || undefined,
        ratingDelivery: ratingDelivery || undefined,
        ratingQuality: ratingQuality || undefined,
        ratingCommunication: ratingCommunication || undefined,
      });
      toast.success("Thank you — your supplier rating was submitted.");
      onSuccess?.(saved);
      onClose();
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Could not submit rating.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const modalUi = (
    <div className="ile-modal-root">
      <div className="ile-modal-overlay" onClick={() => !submitting && onClose()} role="presentation">
        <div
          className="ile-modal-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`supplier-rating-title-${suffix}`}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ile-modal-panel__header">
            <button
              type="button"
              className="ile-modal-panel__close"
              onClick={() => !submitting && onClose()}
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
            <h2 className="ile-modal-panel__title" id={`supplier-rating-title-${suffix}`}>
              Rate Supplier
            </h2>
            <p className="ile-modal-panel__subtitle">
              {supplierName || "Supplier"}
              {poNumber ? ` · PO ${poNumber}` : ""}
            </p>
          </div>

          <form className="ile-modal-panel__form" onSubmit={handleSubmit} noValidate>
            <div className="ile-modal-panel__body">
              <div className="ile-modal-block">
                <p className="small text-muted mb-3">
                  Share feedback about this supplier based on your received order. You can submit one rating per purchase order.
                </p>
                <div className="mb-3">
                  <label className="ile-form-label" htmlFor={`supplier-rating-title-input-${suffix}`}>
                    Review title (optional)
                  </label>
                  <input
                    id={`supplier-rating-title-input-${suffix}`}
                    type="text"
                    className="input-pill"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Short headline for your review"
                    maxLength={512}
                    autoComplete="off"
                  />
                </div>
                <StarRow label="Overall rating" value={rating} onChange={setRating} required />
                <StarRow label="Delivery" value={ratingDelivery} onChange={setRatingDelivery} />
                <StarRow label="Product quality" value={ratingQuality} onChange={setRatingQuality} />
                <StarRow label="Communication" value={ratingCommunication} onChange={setRatingCommunication} />
                <div className="mb-3">
                  <label className="ile-form-label" htmlFor={`supplier-rating-comment-${suffix}`}>
                    Comments (optional)
                  </label>
                  <textarea
                    id={`supplier-rating-comment-${suffix}`}
                    className="input-pill"
                    style={{ minHeight: 100, resize: "vertical" }}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="What went well? What could improve?"
                    maxLength={4000}
                  />
                </div>
              </div>
            </div>

            <div className="ile-modal-panel__footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="ile-modal-btn-primary" disabled={submitting}>
                {submitting ? "Submitting…" : "Submit rating"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  if (typeof document !== "undefined" && document.body) {
    return createPortal(modalUi, document.body);
  }
  return modalUi;
}
